import { useEffect, useMemo } from 'react';
import type { FormInstance } from 'antd';
import { useGetRateCardListQuery } from '@/api';
import { findRateCardByProduct } from '../utils/findRateCardByProduct';
import {
  hasConflictingRateCardBenchmarks,
  resolveRateCardBenchmark
} from '../utils/resolveRateCardBenchmark';
import type { ChannelForm } from '../components/ChannelForm/ChannelForm.types';

type UseRateCardBenchmarkSyncArgs = {
  form: FormInstance<ChannelForm>;
  channel?: string;
  product?: string;
  kpi?: string;
  formats?: string[];
  rateSource?: 'MANUAL' | 'PACKAGE' | 'TECH_FEE';
};

export function useRateCardBenchmarkSync({
  form,
  channel,
  product,
  kpi,
  formats,
  rateSource
}: UseRateCardBenchmarkSyncArgs) {
  const isRateCardMode = rateSource !== 'PACKAGE' && rateSource !== 'TECH_FEE';

  const { data: rateCardsList, isFetching } = useGetRateCardListQuery(
    {
      page: 1,
      limit: 2000,
      order: 1,
      channel: channel ? [channel] : [],
      showDraft: true
    },
    { skip: !isRateCardMode || !channel?.trim() }
  );

  const productRateCard = useMemo(() => {
    if (!isRateCardMode || !channel || !product) return undefined;
    return findRateCardByProduct(rateCardsList?.data ?? [], { channel, product });
  }, [isRateCardMode, rateCardsList?.data, channel, product]);

  const benchmark = useMemo(() => {
    if (!isRateCardMode || !channel || !product || !kpi) return undefined;
    return resolveRateCardBenchmark(rateCardsList?.data ?? [], { channel, product, kpi, formats });
  }, [isRateCardMode, rateCardsList?.data, channel, product, kpi, formats]);

  const hasBenchmarkConflict = useMemo(() => {
    if (!isRateCardMode || !channel || !product || !kpi) return false;
    return hasConflictingRateCardBenchmarks(rateCardsList?.data ?? [], { channel, product, kpi, formats });
  }, [isRateCardMode, rateCardsList?.data, channel, product, kpi, formats]);

  useEffect(() => {
    if (!isRateCardMode) {
      form.setFieldValue('benchmark', undefined);
      return;
    }
    form.setFieldValue('benchmark', benchmark);
  }, [benchmark, form, isRateCardMode]);

  useEffect(() => {
    if (!isRateCardMode || !product || !productRateCard) return;

    const updates: Record<string, unknown> = {};
    if (productRateCard.buyingMetric) {
      updates.buyingMetric = productRateCard.buyingMetric;
    }

    const currentSellRate = form.getFieldValue('sellRate');
    const sellRateEmpty =
      currentSellRate === undefined || currentSellRate === null || currentSellRate === '';
    if (sellRateEmpty && productRateCard.sellRate != null && Number.isFinite(Number(productRateCard.sellRate))) {
      updates.sellRate = Number(productRateCard.sellRate);
    }

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue(updates);
    }
  }, [form, isRateCardMode, product, productRateCard]);

  return { benchmark, hasBenchmarkConflict, isRateCardBenchmarkLoading: isFetching };
}
